import React from 'react';
import { makeStyles } from '@mui/styles';
import { TableHead, TableRow, TableCell, IconButton, Tooltip, Box } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { useTheme } from '@mui/material/styles';

const VIEWS = ['current', 'goal', 'change'];
const CALORIES_TOOLTIP = '(% of Daily Calories)';
const CHEVRON_ICON_STYLE = { width: '24px', height: '24px' };

const useStyles = makeStyles((theme) => ({
  tableHead: {
    ...theme.globalClasses.adminTableHead,
    marginLeft: '10px',
    width: '100%',
  },
  tableHeadCell: {
    height: '56px',
    padding: '8px 16px',
    verticalAlign: 'center',
  },
  firstColumn: {
    width: '163px',
    maxWidth: '163px',
  },
  mobileDataColumn: {
    width: '349px',
    maxWidth: '349px',
    textAlign: 'left',
    scrollSnapMarginLeft: '10px',
  },
  currentColumn: {
    width: '349px',
    maxWidth: '349px',
    textAlign: 'left',
  },
  goalsColumn: {
    width: '198px',
    maxWidth: '198px',
    paddingLeft: '0px',
  },
  changeColumn: {
    width: 'auto',
    maxWidth: 'auto',
    textAlign: 'center',
    paddingLeft: '0px',
  },
  title: {
    fontSize: '18.72px',
    fontWeight: 700,
    lineHeight: '116.7%',
    textTransform: 'capitalize',
    color: theme.palette.black87,
    marginBottom: '0',
    display: 'flex',
    alignItems: 'center',
    width: '143px',
  },
  currentText: {
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    width: '65px',
    marginRight: '8px',
  },
  lighterText: {
    fontWeight: 400,
  },
  mobileHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '0 8px',
    maxWidth: '313px',
  },
  mobileTitleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    padding: '0 10px',
    width: '163px',
    maxWidth: '163px',
  },
  chevronButton: {
    minWidth: '22px',
    width: '22px',
    height: '22px',
    padding: '0',
    display: 'flex',
  },
  infoIcon: {
    ...theme.globalClasses.infoRoundedIcon,
    width: '20px',
    height: '20px',
  },
  viewControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  viewTitle: {
    minWidth: '100px',
    textAlign: 'center',
  },
  navigationSection: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    width: '140px',
    height: '22px',
    position: 'relative',
    marginRight: '8px',
    justifyContent: 'flex-end',
    gap: '20px',
  },
  titleWithCurrent: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  mobileTableTitleCellWithoutIdeal: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '56px',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 16px',
  },
}));

const MacronutrientHeader = ({ variant = 'desktop', onViewChange, currentView = 'current', hasIdeal = true }) => {
  const classes = useStyles();
  const theme = useTheme();

  const handleNextView = () => {
    const currentIndex = VIEWS.indexOf(currentView);
    const nextIndex = (currentIndex + 1) % VIEWS.length;
    onViewChange(VIEWS[nextIndex]);
  };

  const handlePrevView = () => {
    const currentIndex = VIEWS.indexOf(currentView);
    const prevIndex = (currentIndex - 1 + VIEWS.length) % VIEWS.length;
    onViewChange(VIEWS[prevIndex]);
  };

  const renderViewTitle = () => {
    const getTooltipContent = (view) => (
      <span className={classes.currentText}>
        {view.charAt(0).toUpperCase() + view.slice(1)}
        {view !== 'change' && (
          <Tooltip
            title={CALORIES_TOOLTIP}
            arrow
            placement="top"
          >
            <InfoRoundedIcon className={classes.infoIcon} />
          </Tooltip>
        )}
      </span>
    );

    return getTooltipContent(currentView);
  };

  const renderNavigation = () => {
    const chevronStyle = { ...CHEVRON_ICON_STYLE, color: theme.palette.black30 };

    return (
      <Box sx={{ display: 'flex' }}>
        {currentView !== 'current' && (
          <IconButton
            className={classes.chevronButton}
            onClick={handlePrevView}
          >
            <ChevronLeftIcon sx={chevronStyle} />
          </IconButton>
        )}
        {currentView !== 'change' && (
          <IconButton
            className={classes.chevronButton}
            onClick={handleNextView}
          >
            <ChevronRightIcon sx={chevronStyle} />
          </IconButton>
        )}
      </Box>
    );
  };

  const renderTableCell = (className, content, colSpan) => (
    <TableCell
      className={`${classes.tableHeadCell} ${className}`}
      colSpan={colSpan}
    >
      {content}
    </TableCell>
  );

  if (variant === 'mobile') {
    return (
      <TableHead className={classes.tableHead}>
        <TableRow>
          {renderTableCell(
            '',
            hasIdeal ? (
              <div className={classes.mobileHeader}>
                <div className={classes.mobileTitleSection}>
                  <span className={classes.title}>Macronutrients</span>
                  {renderViewTitle()}
                </div>
                {renderNavigation()}
              </div>
            ) : (
              <div className={classes.mobileTableTitleCellWithoutIdeal}>
                <span className={classes.title}>Macronutrients</span>
                <span style={{ fontWeight: 600, marginRight: '10px' }}>Intake</span>
              </div>
            ),
          )}
        </TableRow>
      </TableHead>
    );
  }

  return (
    <TableHead className={classes.tableHead}>
      <TableRow>
        {renderTableCell(
          classes.firstColumn,
          <div className={classes.titleWithCurrent}>
            <span className={classes.title}>Macronutrients</span>
          </div>,
        )}
        {renderTableCell(
          classes.currentColumn,
          <>
            Intake <span className={classes.lighterText}>{CALORIES_TOOLTIP}</span>
          </>,
          hasIdeal ? 2 : 1,
        )}
        {hasIdeal && (
          <>
            {renderTableCell(
              classes.goalsColumn,
              <>
                Goal <span className={classes.lighterText}>{CALORIES_TOOLTIP}</span>
              </>,
            )}
            {renderTableCell(classes.changeColumn, 'Change')}
          </>
        )}
      </TableRow>
    </TableHead>
  );
};

export default MacronutrientHeader;
